import React, { useEffect, useState } from "react";

interface ColorInputProps {
	value: string;
	onChange: (value: string) => void;
	label?: string;
	withOpacity?: boolean;
	className?: string;
}

// Helper function to convert hex to rgba
const hexToRgba = (hex: string, alpha: number = 1): string => {
	// Remove # if present
	hex = hex.replace("#", "");

	// Convert 3-digit hex to 6-digit
	if (hex.length === 3) {
		hex = hex
			.split("")
			.map((char) => char + char)
			.join("");
	}

	// Parse hex values to RGB
	const r = parseInt(hex.substring(0, 2), 16);
	const g = parseInt(hex.substring(2, 4), 16);
	const b = parseInt(hex.substring(4, 6), 16);

	// Return rgba string
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Helper function to parse rgba string
const parseRgba = (
	rgba: string
): { r: number; g: number; b: number; a: number } | null => {
	const match = rgba.match(
		/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d*\.?\d+))?\)/
	);
	if (match) {
		return {
			r: parseInt(match[1]),
			g: parseInt(match[2]),
			b: parseInt(match[3]),
			a: match[4] ? parseFloat(match[4]) : 1,
		};
	}
	return null;
};

// Helper function to convert rgba to hex
const rgbaToHex = (rgba: string): { hex: string; alpha: number } => {
	const parsed = parseRgba(rgba);

	if (parsed) {
		const hex =
			"#" +
			((1 << 24) + (parsed.r << 16) + (parsed.g << 8) + parsed.b)
				.toString(16)
				.slice(1);

		return { hex, alpha: parsed.a };
	}

	return { hex: "#000000", alpha: 1 };
};

const ColorInput: React.FC<ColorInputProps> = ({
	value,
	onChange,
	label,
	withOpacity = false,
	className = "",
}) => {
	// Initialize state
	const [colorValue, setColorValue] = useState("#000000");
	const [alphaValue, setAlphaValue] = useState(1);

	// Parse initial value
	useEffect(() => {
		if (value.startsWith("rgba")) {
			const { hex, alpha } = rgbaToHex(value);
			setColorValue(hex);
			setAlphaValue(alpha);
		} else {
			setColorValue(value);
		}
	}, []);

	// Handle color change
	const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newColor = e.target.value;
		setColorValue(newColor);

		if (withOpacity) {
			onChange(hexToRgba(newColor, alphaValue));
		} else {
			onChange(newColor);
		}
	};

	// Handle opacity change
	const handleOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newAlpha = parseFloat(e.target.value);
		setAlphaValue(newAlpha);
		onChange(hexToRgba(colorValue, newAlpha));
	};

	return (
		<div className={`${className}`}>
			{label && (
				<label className="block text-sm font-medium mb-1">
					{label}
				</label>
			)}
			<div className="flex items-center gap-2">
				<input
					type="color"
					value={colorValue}
					onChange={handleColorChange}
					className="w-10 h-10"
				/>
				<input
					type="text"
					value={colorValue}
					onChange={(e) => {
						setColorValue(e.target.value);
						if (withOpacity) {
							onChange(hexToRgba(e.target.value, alphaValue));
						} else {
							onChange(e.target.value);
						}
					}}
					className="flex-1 p-2 border rounded"
				/>

				{withOpacity && (
					<div className="flex flex-col ml-2 w-16">
						<span className="text-xs text-gray-500 mb-1">
							Opacity
						</span>
						<input
							type="range"
							min="0"
							max="1"
							step="0.01"
							value={alphaValue}
							onChange={handleOpacityChange}
							className="w-full"
						/>
						<span className="text-xs text-center">
							{Math.round(alphaValue * 100)}%
						</span>
					</div>
				)}
			</div>
		</div>
	);
};

export default ColorInput;
